/**
 * PRProgressionChart — Line chart showing exercise weight over time.
 * Pure RN Views, no external chart library.
 */
import { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';

interface Props {
  exerciseName: string;
  userId: string;
  height?: number;
  unit?: string;
}

interface DataPoint {
  date: string;
  weight: number;
}

export default function PRProgressionChart({
  exerciseName,
  userId,
  height = 140,
  unit = 'lbs',
}: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [exerciseName, userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load up to 1 year of data
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      const { data: events } = await supabase
        .from('strength_events')
        .select('weight, performed_at')
        .eq('profile_id', userId)
        .ilike('lift_key', `%${exerciseName.toLowerCase().replace(/[^a-z]/g, '_')}%`)
        .gte('performed_at', oneYearAgo.toISOString())
        .order('performed_at', { ascending: true });

      if (events && events.length > 0) {
        setData(events.map((e: any) => ({ date: e.performed_at, weight: e.weight })));
      } else {
        // Fallback: scan workouts
        const { data: workouts } = await supabase
          .from('workouts')
          .select('performed_at, payload')
          .eq('user_id', userId)
          .gte('performed_at', oneYearAgo.toISOString())
          .order('performed_at', { ascending: true });

        const points: DataPoint[] = [];
        for (const w of workouts ?? []) {
          let payload = w.payload;
          if (typeof payload === 'string') {
            try { payload = JSON.parse(payload); } catch { continue; }
          }
          for (const ex of payload?.exercises ?? []) {
            if (ex.name?.toLowerCase() === exerciseName.toLowerCase()) {
              const maxWeight = Math.max(...(ex.sets ?? []).map((s: any) => s.weight || 0));
              if (maxWeight > 0) {
                points.push({ date: w.performed_at, weight: maxWeight });
              }
            }
          }
        }
        setData(points);
      }
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const { width: screenWidth } = useWindowDimensions();
  const chartWidth = Math.min(screenWidth - 48, 560); // cap at 560 on tablets
  const padding = 10;
  const innerWidth = chartWidth - padding * 2;
  const innerHeight = height - 40;

  const { points, maxWeight, minWeight, prIndex, xLabels } = useMemo(() => {
    if (data.length < 1) return { points: [], maxWeight: 0, minWeight: 0, prIndex: -1, xLabels: [] };

    const weights = data.map((d) => d.weight);
    const max = Math.max(...weights);
    const min = Math.min(...weights);
    const range = max - min || 1;

    let prIdx = 0;
    weights.forEach((w, i) => { if (w >= weights[prIdx]) prIdx = i; });

    const pts = data.map((d, i) => ({
      x: padding + (data.length === 1 ? innerWidth / 2 : (i / (data.length - 1)) * innerWidth),
      y: 20 + innerHeight - ((d.weight - min) / range) * (innerHeight - 10),
    }));

    // X-axis labels (first, middle, last)
    const labels: { x: number; label: string }[] = [];
    const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (data.length >= 1) labels.push({ x: pts[0].x, label: fmt(data[0].date) });
    if (data.length >= 3) {
      const mid = Math.floor(data.length / 2);
      labels.push({ x: pts[mid].x, label: fmt(data[mid].date) });
    }
    if (data.length >= 2) labels.push({ x: pts[pts.length - 1].x, label: fmt(data[data.length - 1].date) });

    return { points: pts, maxWeight: max, minWeight: min, prIndex: prIdx, xLabels: labels };
  }, [data, innerWidth, innerHeight]);

  if (loading) {
    return (
      <View style={[styles.container, { height: 60 }]}>
        <Text style={[styles.loadingText, { color: c.textTertiary }]}>Loading chart...</Text>
      </View>
    );
  }

  if (data.length < 2) {
    return (
      <View style={[styles.container, { height: 50 }]}>
        <Text style={[styles.loadingText, { color: c.textTertiary }]}>
          {data.length === 0 ? 'No data in last 3 months' : 'Need 2+ data points for chart'}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* PR label */}
      <Text style={[styles.prLabel, { color: c.warning }]}>
        Best in Last 12 Months: {maxWeight} {unit}
      </Text>

      <View style={[styles.chartArea, { width: chartWidth, height }]}>
        {/* Line segments */}
        {points.map((pt, i) => {
          if (i === 0) return null;
          const prev = points[i - 1];
          const dx = pt.x - prev.x;
          const dy = pt.y - prev.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          return (
            <View
              key={`line-${i}`}
              style={{
                position: 'absolute',
                left: prev.x,
                top: prev.y - 1,
                width: length,
                height: 2,
                backgroundColor: c.accent,
                borderRadius: 1,
                transform: [{ rotate: `${angle}deg` }],
                transformOrigin: 'left center',
              }}
            />
          );
        })}

        {/* Dots */}
        {points.map((pt, i) => {
          const isPR = i === prIndex;
          const dotSize = isPR ? 10 : 5;
          return (
            <View key={`dot-${i}`}>
              <View
                style={{
                  position: 'absolute',
                  left: pt.x - dotSize / 2,
                  top: pt.y - dotSize / 2,
                  width: dotSize,
                  height: dotSize,
                  borderRadius: dotSize / 2,
                  backgroundColor: isPR ? c.warning : c.accent,
                }}
              />
              {isPR && (
                <Text
                  style={{
                    position: 'absolute',
                    left: pt.x - 6,
                    top: pt.y - 18,
                    fontSize: 12,
                    color: c.warning,
                  }}
                >
                  ★
                </Text>
              )}
            </View>
          );
        })}

        {/* X-axis labels */}
        {xLabels.map((label, i) => (
          <Text
            key={`xlabel-${i}`}
            style={{
              position: 'absolute',
              left: label.x - 24,
              bottom: 0,
              fontSize: 11,
              color: c.textTertiary,
              width: 48,
              textAlign: 'center',
            }}
          >
            {label.label}
          </Text>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  prLabel: {
    fontSize: 12,
    fontWeight: '800',
    marginBottom: 4,
  },
  chartArea: {
    position: 'relative',
  },
  loadingText: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
});
